import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Edit3, Trash2, ExternalLink, Star, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListAffiliates,
  adminCreateAffiliate,
  adminUpdateAffiliate,
  adminDeleteAffiliate,
  formatApiError,
} from "../../lib/api";

const CATEGORIES = [
  { slug: "hotels", label: "Hotels & Stays" },
  { slug: "airlines", label: "Flights" },
  { slug: "cruises", label: "Cruises" },
  { slug: "credit-cards", label: "Credit Cards" },
];

const EMPTY = {
  id: "",
  slug: "",
  name: "",
  category: "hotels",
  pitch: "",
  highlight: "",
  logo: "",
  affiliate_url: "",
  active: true,
  featured: false,
  order: 0,
};

export default function AdminAffiliates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | {slug, ...}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminListAffiliates();
      setItems(list);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const totalClicks = useMemo(
    () => items.reduce((s, a) => s + (a.clicks || 0), 0),
    [items]
  );
  const shown = useMemo(
    () => (filter === "all" ? items : items.filter((a) => a.category === filter)),
    [items, filter]
  );

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (a) => setEditing({ ...EMPTY, ...a });
  const close = () => setEditing(null);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await adminDeleteAffiliate(slug);
      setItems((xs) => xs.filter((x) => x.slug !== slug));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    const isNew = !items.find((a) => a.slug === editing.slug);
    const { id, clicks, created_at, _updated_at, ...payload } = editing;
    try {
      let saved;
      if (isNew) {
        saved = await adminCreateAffiliate(payload);
      } else {
        saved = await adminUpdateAffiliate(editing.slug, payload);
      }
      setItems((xs) => {
        const rest = xs.filter((x) => x.slug !== editing.slug && x.slug !== payload.slug);
        return [...rest, { ...saved, clicks: saved.clicks ?? 0 }];
      });
      toast.success(isNew ? "Created" : "Saved");
      close();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-affiliates-page">
      <div className="flex items-end justify-between gap-5 flex-wrap mb-8">
        <div>
          <p className="text-[11px] tracking-[0.25em] uppercase text-stone-500 font-semibold">Monetization</p>
          <h1 className="font-serif text-4xl mt-2 text-stone-900">Affiliate partners</h1>
          <p className="mt-3 text-stone-600 text-sm max-w-xl">
            Paste your tracking URLs from each affiliate program. Links are redirected through{" "}
            <code className="text-[12px] bg-stone-100 px-1.5 py-0.5">/api/go/&lt;slug&gt;</code> so clicks are counted.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] tracking-[0.25em] uppercase text-stone-500">Total clicks</p>
            <p className="font-serif text-2xl text-stone-900 tabular-nums" data-testid="affiliates-total-clicks">
              {totalClicks}
            </p>
          </div>
          <button
            type="button"
            onClick={openNew}
            data-testid="affiliate-new-button"
            className="inline-flex items-center gap-2 bg-stone-900 text-white text-[12px] tracking-[0.15em] uppercase font-semibold px-5 py-3 hover:bg-[#9c4122] transition-colors"
          >
            <Plus size={14} /> Add partner
          </button>
        </div>
      </div>

      <div className="flex items-center gap-x-6 gap-y-2 flex-wrap pb-4 mb-6 border-b border-stone-200">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="aff-filter-all">
          All ({items.length})
        </FilterPill>
        {CATEGORIES.map((c) => {
          const n = items.filter((a) => a.category === c.slug).length;
          return (
            <FilterPill
              key={c.slug}
              active={filter === c.slug}
              onClick={() => setFilter(c.slug)}
              testid={`aff-filter-${c.slug}`}
            >
              {c.label} ({n})
            </FilterPill>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-stone-500 py-12">
          <Loader2 className="animate-spin" size={16} /> Loading partners…
        </div>
      ) : shown.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-stone-300">
          <p className="text-stone-700">No partners in this category yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="affiliates-table">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-stone-500 border-b border-stone-200">
                <th className="py-3 pr-4">Partner</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3 pr-4 text-right">Clicks</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown
                .sort((a, b) => a.category.localeCompare(b.category) || (a.order ?? 99) - (b.order ?? 99))
                .map((a) => (
                  <tr
                    key={a.slug}
                    data-testid={`affiliate-row-${a.slug}`}
                    className="border-b border-stone-100 hover:bg-stone-50"
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <TableLogo a={a} />
                        <div>
                          <p className="font-medium text-stone-900">
                            {a.name}
                            {a.featured && <Star size={11} className="inline ml-1.5 fill-[#9c4122] text-[#9c4122]" />}
                          </p>
                          <p className="text-[11px] text-stone-500">{a.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-stone-700">
                      {CATEGORIES.find((c) => c.slug === a.category)?.label || a.category}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1.5 text-stone-700">
                        <BarChart3 size={12} /> {a.clicks || 0}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm ${
                          a.active ? "bg-green-50 text-green-800" : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {a.active ? "Active" : "Hidden"}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <a
                          href={a.affiliate_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-stone-500 hover:text-stone-900"
                          title="Open URL"
                        >
                          <ExternalLink size={14} />
                        </a>
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          data-testid={`affiliate-edit-${a.slug}`}
                          className="p-1.5 text-stone-500 hover:text-stone-900"
                          title="Edit"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(a.slug)}
                          data-testid={`affiliate-delete-${a.slug}`}
                          className="p-1.5 text-stone-500 hover:text-[#9c4122]"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditorModal
          value={editing}
          onChange={setEditing}
          onSave={onSave}
          onCancel={close}
          saving={saving}
        />
      )}
    </div>
  );
}

function TableLogo({ a }) {
  const [failed, setFailed] = React.useState(false);
  return (
    <div className="w-9 h-9 bg-stone-50 border border-stone-100 rounded-sm flex items-center justify-center overflow-hidden flex-shrink-0">
      {a.logo && !failed ? (
        <img src={a.logo} alt="" onError={() => setFailed(true)} className="max-w-[80%] max-h-[80%] object-contain" />
      ) : (
        <span className="text-stone-400 font-serif">{a.name[0]}</span>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[12px] tracking-wide pb-1 border-b transition-colors ${
        active
          ? "text-[#9c4122] border-[#9c4122] font-semibold"
          : "text-stone-600 border-transparent hover:text-stone-900"
      }`}
    >
      {children}
    </button>
  );
}

function EditorModal({ value, onChange, onSave, onCancel, saving }) {
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });
  const disabled = !v.slug || !v.name || !v.affiliate_url || saving;

  return (
    <div
      data-testid="affiliate-editor"
      className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl text-stone-900">
              {v.clicks !== undefined ? "Edit partner" : "New partner"}
            </h2>
            <button onClick={onCancel} className="text-stone-500 text-sm">
              Cancel
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name*">
              <input
                data-testid="aff-input-name"
                value={v.name}
                onChange={(e) => set({ name: e.target.value })}
                className="input"
              />
            </Field>

            <Field label="Slug* (URL-friendly, no spaces)">
              <input
                data-testid="aff-input-slug"
                value={v.slug}
                onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                className="input font-mono text-[13px]"
              />
            </Field>

            <Field label="Category*">
              <select
                data-testid="aff-input-category"
                value={v.category}
                onChange={(e) => set({ category: e.target.value })}
                className="input"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Display order (lower = first)">
              <input
                type="number"
                data-testid="aff-input-order"
                value={v.order}
                onChange={(e) => set({ order: Number(e.target.value) || 0 })}
                className="input"
              />
            </Field>

            <Field label="Highlight (short badge text)">
              <input
                data-testid="aff-input-highlight"
                value={v.highlight}
                onChange={(e) => set({ highlight: e.target.value })}
                placeholder="e.g. 60,000-pt welcome bonus"
                className="input"
              />
            </Field>

            <Field label="Logo URL">
              <input
                data-testid="aff-input-logo"
                value={v.logo}
                onChange={(e) => set({ logo: e.target.value })}
                placeholder="https://…"
                className="input"
              />
            </Field>

            <Field label="Affiliate URL*" cols={2}>
              <input
                data-testid="aff-input-url"
                value={v.affiliate_url}
                onChange={(e) => set({ affiliate_url: e.target.value })}
                placeholder="https://partner.booking.com/…?aid=YOUR_TRACKING_ID"
                className="input font-mono text-[13px]"
              />
              <p className="mt-1 text-[11px] text-stone-500">
                Users will hit <code>/api/go/{v.slug || "<slug>"}</code> — the backend tracks the click then 302-redirects here.
              </p>
            </Field>

            <Field label="Pitch / description" cols={2}>
              <textarea
                data-testid="aff-input-pitch"
                value={v.pitch}
                onChange={(e) => set({ pitch: e.target.value })}
                rows={3}
                className="input"
                placeholder="A short, honest reason to use this partner."
              />
            </Field>

            <div className="sm:col-span-2 flex items-center gap-8 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="aff-input-active"
                  checked={v.active}
                  onChange={(e) => set({ active: e.target.checked })}
                />
                <span className="text-sm text-stone-800">Active (visible to readers)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="aff-input-featured"
                  checked={v.featured}
                  onChange={(e) => set({ featured: e.target.checked })}
                />
                <span className="text-sm text-stone-800">Featured (Top pick)</span>
              </label>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <button
              onClick={onCancel}
              className="text-[12px] tracking-[0.15em] uppercase text-stone-600 px-5 py-3"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={disabled}
              data-testid="aff-save-button"
              className="inline-flex items-center gap-2 bg-stone-900 text-white text-[12px] tracking-[0.15em] uppercase font-semibold px-6 py-3 hover:bg-[#9c4122] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : null}
              Save
            </button>
          </div>
        </div>
      </div>
      <style>{`
        .input {
          width: 100%;
          border: 1px solid #d6d3d1;
          background: #fff;
          padding: 0.625rem 0.75rem;
          font-size: 14px;
          color: #292524;
          outline: none;
        }
        .input:focus {
          border-color: #9c4122;
        }
      `}</style>
    </div>
  );
}

function Field({ label, cols = 1, children }) {
  return (
    <div className={cols === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-stone-500 font-semibold mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
